import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Loader2, Search, X } from 'lucide-react';
import vendorApi from '../../../api/vendorApi';
import { QboCustomer, QboVendor, VendorAlias, VendorAccountData, VendorAliasListResponse } from '../../../api/types';
import VendorAliasModal from './VendorAliasModal';

type VendorDirectorySource = 'aliases' | 'qbo' | 'qbo_customers';

interface VendorDirectoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    source: VendorDirectorySource;
    businessId?: string;
}

type SelectedVendorCard =
    | { source: 'aliases'; vendorName: string; records: VendorAlias[] }
    | { source: 'qbo'; item: QboVendor }
    | { source: 'qbo_customers'; item: QboCustomer };

const getAliasName = (alias: VendorAlias): string => (
    alias.clean_vendor_name || alias.vendor_name || alias.raw_norm_name || 'Unnamed Vendor'
);

const getAliasNormalized = (alias: VendorAlias): string => (
    alias.normalized_description || alias.raw_norm_name || '—'
);

const renderMapping = (label: string, mapping?: VendorAccountData) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
        {mapping ? (
            <div className="mt-2 space-y-1 text-sm text-gray-800">
                <div><span className="font-medium text-gray-900">Name:</span> {mapping.account_name || mapping.name || '—'}</div>
                <div><span className="font-medium text-gray-900">ID:</span> {mapping.account_id || mapping.id || '—'}</div>
                <div><span className="font-medium text-gray-900">Bucket:</span> {mapping.fiscal_bucket || '—'}</div>
            </div>
        ) : (
            <div className="mt-2 text-sm text-gray-500">No mapping data</div>
        )}
    </div>
);

const VendorDirectoryModal: React.FC<VendorDirectoryModalProps> = ({ isOpen, onClose, source, businessId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedVendor, setSelectedVendor] = useState<SelectedVendorCard | null>(null);
    const [selectedAliasLookup, setSelectedAliasLookup] = useState<{ rawNormName: string; direction: string } | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setDebouncedSearchTerm('');
            setSelectedVendor(null);
            setSelectedAliasLookup(null);
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 250);
        return () => window.clearTimeout(timer);
    }, [searchTerm]);

    const aliasQuery = useQuery({
        queryKey: ['ledger-vendor-directory', 'aliases', debouncedSearchTerm, businessId],
        queryFn: async (): Promise<VendorAliasListResponse> => {
            const pageSize = 500;
            let page = 1;
            let total = 0;
            const allItems: VendorAlias[] = [];

            while (page <= 20) {
                const response = await vendorApi.getAliases(
                    undefined,
                    debouncedSearchTerm || undefined,
                    page,
                    pageSize,
                    'clean_vendor_name',
                    'asc',
                    undefined,
                    undefined,
                    businessId
                );
                if (!total) total = response.total || 0;
                allItems.push(...(response.items || []));
                if ((response.items || []).length < pageSize || allItems.length >= total) {
                    break;
                }
                page += 1;
            }

            return {
                items: allItems,
                total: total || allItems.length,
                page: 1,
                page_size: allItems.length,
            };
        },
        enabled: isOpen && source === 'aliases',
    });

    const qboQuery = useQuery({
        queryKey: ['ledger-vendor-directory', 'qbo', debouncedSearchTerm, businessId],
        queryFn: () => vendorApi.getQboVendorsList(
            debouncedSearchTerm || undefined,
            1,
            250,
            businessId
        ),
        enabled: isOpen && source === 'qbo',
    });

    const qboCustomersQuery = useQuery({
        queryKey: ['ledger-vendor-directory', 'qbo_customers', debouncedSearchTerm, businessId],
        queryFn: () => vendorApi.getQboCustomersList(
            debouncedSearchTerm || undefined,
            1,
            250,
            businessId
        ),
        enabled: isOpen && source === 'qbo_customers',
    });

    const isLoading = source === 'aliases' ? aliasQuery.isLoading : source === 'qbo' ? qboQuery.isLoading : qboCustomersQuery.isLoading;
    const hasError = source === 'aliases' ? aliasQuery.isError : source === 'qbo' ? qboQuery.isError : qboCustomersQuery.isError;

    const aliasItems = aliasQuery.data?.items;
    const qboItems = qboQuery.data?.items || [];
    const qboCustomerItems = qboCustomersQuery.data?.items || [];

    const groupedAliases = useMemo(() => {
        const groups = new Map<string, VendorAlias[]>();
        (aliasItems || []).forEach((alias) => {
            const key = (alias.vendor_name || alias.clean_vendor_name || alias.raw_norm_name || 'Unnamed Vendor').trim();
            const current = groups.get(key) || [];
            current.push(alias);
            groups.set(key, current);
        });
        return Array.from(groups.entries())
            .map(([vendorName, records]) => ({
                vendorName,
                records: records.sort((a, b) => {
                    const directionSort = (a.direction || '').localeCompare(b.direction || '');
                    if (directionSort !== 0) return directionSort;
                    return (a.raw_norm_name || '').localeCompare(b.raw_norm_name || '');
                }),
            }))
            .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
    }, [aliasItems]);

    const aliasRecordCount = aliasQuery.data?.total || (aliasItems || []).length;
    const totalCount = source === 'aliases'
        ? groupedAliases.length
        : source === 'qbo'
            ? (qboQuery.data?.total || qboItems.length)
            : (qboCustomersQuery.data?.total || qboCustomerItems.length);
    const rowsCount = source === 'aliases' ? groupedAliases.length : source === 'qbo' ? qboItems.length : qboCustomerItems.length;

    if (!isOpen) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-[65] overflow-y-auto">
                <div className="flex min-h-screen items-center justify-center px-4 py-8">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-70" onClick={onClose} />
                    <div className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl">
                        <div className="border-b border-gray-200 px-5 py-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {source === 'aliases' ? 'Vendor Aliases' : source === 'qbo' ? 'QBO Vendors' : 'QBO Customers'}
                                    </h3>
                                    <p className="mt-0.5 text-sm text-gray-500">
                                        {source === 'aliases'
                                            ? `${totalCount} vendors • ${aliasRecordCount} alias records`
                                            : `${totalCount} records`}
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="relative mt-4">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={source === 'aliases' ? 'Search vendor aliases...' : source === 'qbo' ? 'Search QBO vendors...' : 'Search QBO customers...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="max-h-[62vh] overflow-y-auto p-3">
                            {isLoading && (
                                <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading {source === 'qbo_customers' ? 'customers' : 'vendors'}...
                                </div>
                            )}

                            {!isLoading && hasError && (
                                <div className="flex h-40 items-center justify-center text-sm text-red-600">
                                    Failed to load {source === 'qbo_customers' ? 'customer' : 'vendor'} directory.
                                </div>
                            )}

                            {!isLoading && !hasError && rowsCount === 0 && (
                                <div className="flex h-40 items-center justify-center text-sm text-gray-500">
                                    No {source === 'qbo_customers' ? 'customers' : 'vendors'} found.
                                </div>
                            )}

                            {!isLoading && !hasError && rowsCount > 0 && (
                                <div className="space-y-2">
                                    {source === 'aliases' ? (
                                        groupedAliases.map((group) => (
                                            <button
                                                key={group.vendorName}
                                                onClick={() => setSelectedVendor({ source: 'aliases', vendorName: group.vendorName, records: group.records })}
                                                className="flex w-full items-start justify-between rounded-md border border-gray-200 px-3 py-2 text-left hover:border-indigo-200 hover:bg-indigo-50"
                                            >
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-gray-900">{group.vendorName}</div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                        <span className="rounded bg-gray-100 px-2 py-0.5">{group.records.length} records</span>
                                                        <span className="truncate">
                                                            {Array.from(new Set(group.records.map((record) => record.direction || '—'))).join(' • ')}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="ml-3 shrink-0 text-xs font-medium text-indigo-600">View Card</span>
                                            </button>
                                        ))
                                    ) : source === 'qbo' ? (
                                        qboItems.map((vendor) => (
                                            <button
                                                key={vendor._id}
                                                onClick={() => setSelectedVendor({ source: 'qbo', item: vendor })}
                                                className="flex w-full items-start justify-between rounded-md border border-gray-200 px-3 py-2 text-left hover:border-indigo-200 hover:bg-indigo-50"
                                            >
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-gray-900">{vendor.DisplayName || vendor.CompanyName || 'Unnamed Vendor'}</div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                        <span className="rounded bg-gray-100 px-2 py-0.5">ID: {vendor.Id || '—'}</span>
                                                        <span className="truncate">{vendor.PrimaryEmailAddr?.Address || 'No email'}</span>
                                                    </div>
                                                </div>
                                                <span className="ml-3 shrink-0 text-xs font-medium text-indigo-600">View Card</span>
                                            </button>
                                        ))
                                    ) : (
                                        qboCustomerItems.map((customer) => (
                                            <button
                                                key={customer._id}
                                                onClick={() => setSelectedVendor({ source: 'qbo_customers', item: customer })}
                                                className="flex w-full items-start justify-between rounded-md border border-gray-200 px-3 py-2 text-left hover:border-indigo-200 hover:bg-indigo-50"
                                            >
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-gray-900">{customer.DisplayName || customer.CompanyName || customer.FullyQualifiedName || 'Unnamed Customer'}</div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                        <span className="rounded bg-gray-100 px-2 py-0.5">ID: {customer.Id || '—'}</span>
                                                        <span className="truncate">{customer.PrimaryEmailAddr?.Address || 'No email'}</span>
                                                    </div>
                                                </div>
                                                <span className="ml-3 shrink-0 text-xs font-medium text-indigo-600">View Card</span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {selectedVendor && (
                <div className="fixed inset-0 z-[75] overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center px-4 py-8">
                        <div className="fixed inset-0 bg-gray-800 bg-opacity-45" onClick={() => setSelectedVendor(null)} />
                        <div className="relative w-full max-w-xl rounded-lg bg-white shadow-2xl">
                            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="rounded-full bg-indigo-100 p-2">
                                        <Building2 className="h-4 w-4 text-indigo-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="truncate text-base font-semibold text-gray-900">
                                            {selectedVendor.source === 'aliases'
                                                ? selectedVendor.vendorName
                                                : (selectedVendor.item.DisplayName || selectedVendor.item.CompanyName || selectedVendor.item.FullyQualifiedName || (selectedVendor.source === 'qbo_customers' ? 'Unnamed Customer' : 'Unnamed Vendor'))}
                                        </h4>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            {selectedVendor.source === 'aliases' ? 'Vendor Alias Record' : selectedVendor.source === 'qbo' ? 'QBO Vendor Record' : 'QBO Customer Record'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedVendor(null)}
                                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="space-y-4 px-5 py-4">
                                {selectedVendor.source === 'aliases' ? (
                                    <>
                                        <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                                            <div><span className="font-medium text-gray-900">Vendor Name:</span> {selectedVendor.vendorName}</div>
                                            <div><span className="font-medium text-gray-900">Alias Records:</span> {selectedVendor.records.length}</div>
                                        </div>
                                        <div className="rounded-lg border border-gray-200">
                                            <div className="grid grid-cols-[1.1fr_0.8fr_0.8fr_0.8fr] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                <span>Description</span>
                                                <span>Direction</span>
                                                <span>Bucket</span>
                                                <span>QBO</span>
                                            </div>
                                            <div className="max-h-72 overflow-y-auto">
                                                {selectedVendor.records.map((alias) => (
                                                    <button
                                                        key={alias._id}
                                                        onClick={() => setSelectedAliasLookup({ rawNormName: alias.raw_norm_name, direction: alias.direction || 'outflow' })}
                                                        className="grid w-full grid-cols-[1.1fr_0.8fr_0.8fr_0.8fr] gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-700 last:border-b-0 hover:bg-indigo-50"
                                                    >
                                                        <span className="truncate" title={getAliasNormalized(alias)}>{getAliasNormalized(alias)}</span>
                                                        <span className="capitalize">{alias.direction || '—'}</span>
                                                        <span className="truncate">{alias.finz_data?.fiscal_bucket || '—'}</span>
                                                        <span>{alias.qbo_data?.account_name || alias.qbo_data?.name || '—'}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                                            <div><span className="font-medium text-gray-900">Display Name:</span> {selectedVendor.item.DisplayName || '—'}</div>
                                            <div><span className="font-medium text-gray-900">Vendor ID:</span> {selectedVendor.item.Id || '—'}</div>
                                            <div><span className="font-medium text-gray-900">Company:</span> {selectedVendor.item.CompanyName || '—'}</div>
                                            <div><span className="font-medium text-gray-900">Active:</span> {selectedVendor.item.Active === undefined ? '—' : selectedVendor.item.Active ? 'Yes' : 'No'}</div>
                                            <div><span className="font-medium text-gray-900">Email:</span> {selectedVendor.item.PrimaryEmailAddr?.Address || '—'}</div>
                                            <div><span className="font-medium text-gray-900">Phone:</span> {selectedVendor.item.PrimaryPhone?.FreeFormNumber || selectedVendor.item.Mobile?.FreeFormNumber || '—'}</div>
                                            <div><span className="font-medium text-gray-900">Balance:</span> {selectedVendor.item.Balance ?? '—'}</div>
                                            <div><span className="font-medium text-gray-900">Currency:</span> {selectedVendor.item.CurrencyRef?.name || selectedVendor.item.CurrencyRef?.id || '—'}</div>
                                        </div>
                                        {/* <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Raw QBO Data</div>
                                            <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-700">
                                                {JSON.stringify(selectedVendor.item, null, 2)}
                                            </pre>
                                        </div> */}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedAliasLookup && (
                <VendorAliasModal
                    isOpen={true}
                    onClose={() => setSelectedAliasLookup(null)}
                    rawNormName={selectedAliasLookup.rawNormName}
                    direction={selectedAliasLookup.direction}
                    businessId={businessId}
                />
            )}
        </>,
        document.body
    );
};

export default VendorDirectoryModal;
